import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import { PermissionSetService } from '../../lib/api'
import {
	CreatePermissionSetDataProps,
	GetPermissionSetDataResponse,
	UpdatePermissionSetDataProps,
} from '../../lib/api/types/permissionSet.interfaces'
import { getErrorData, handleApiError } from '../../lib/utils/handleApiError'
import toastCaller from '../../lib/utils/toastCaller'

export const usePermissionSet = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetPermissionSetDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetPermissionSetDataResponse>({
		queryKey: ['permissionSet', id],
		queryFn: () => PermissionSetService.get({ id }),
		enabled: isAuthenticated && Boolean(id),
		staleTime: 1000 * 30,
		...options,
	})
}

export const useCreatePermissionSet = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['permissionSetCreate'],
		mutationFn: ({
			data,
			invalidate,
		}: {
			data: CreatePermissionSetDataProps
			invalidate?: boolean
		}) => PermissionSetService.create({ data }),
		onError: (error, { data }) => handleApiError(error, data?.name),
		onSuccess: (response, { data, invalidate = true }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['organizationPermissionSets'],
					exact: false,
				})
			}
			toastCaller({
				type: 'success',
				message: `Permission set for ${data?.name} created successfully!`,
			})
			return response
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdatePermissionSet = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['permissionSetUpdate'],
		mutationFn: ({
			data,
			id,
			invalidate,
		}: {
			data: UpdatePermissionSetDataProps
			id: string
			invalidate?: boolean
		}) => PermissionSetService.update({ data, id }),
		onSuccess: (_, { id, invalidate = true }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['organizationPermissionSets'],
					exact: false,
				})
				for (const queryKey of [
					['permissionSet', id],
					['users'],
					['objectPermissions'],
					['objectComputedPermissions'],
					['objectUserPermissionLevel'],
				]) {
					queryClient.invalidateQueries({ queryKey })
				}
			}
			toastCaller({
				type: 'success',
				message: `Permission set updated successfully!`,
			})
		},
		onError: (error, { data }) => {
			const { errorMessageCode } = getErrorData(error)
			handleApiError(
				error,
				data?.name,
				errorMessageCode === 'PERMISSION_SET_CONFIRMATION_REQUIRED'
					? 'warning'
					: 'error',
			)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeletePermissionSet = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['permissionSetDelete'],
		mutationFn: ({ id }: DefaultGetParamsProps) =>
			PermissionSetService.delete({ id }),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organizationPermissionSets'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
